import React from "react";
import Box from "@material-ui/core/Box";
import Paper from "@material-ui/core/Paper";
import InputBase from "@material-ui/core/InputBase";
import SearchIcon from "@material-ui/icons/Search";
import IconButton from "@material-ui/core/IconButton";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import FormGroup from "@material-ui/core/FormGroup";
import InputLabel from "@material-ui/core/InputLabel";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import LinearProgress from "@material-ui/core/LinearProgress";
import InfiniteScroll from "react-infinite-scroller";
import { apiPath } from "api";
import { generalAuthTokenHeader, multimediaAuthTokenHeader } from "__helpers/auth-header";
import { userService } from "_services/user.service";
import { DEFAULT_PER_PAGE_RECORDS } from "__helpers/constants";
import moment from "moment";
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";

// Components
import CreatePatientModal from "./components/CreatePatientModal";
import EditPatientModal from "./components/EditPatientModal";
import PatientDetailsPanel from "./components/PatientDetailsPanel";
import PatientTabs from "components/PatientTabs";
import ContentViewerPanel from "views/providerViews/PatientDashboard/ContentViewerPanel";

// Styles
import "./myPatients.css";

// Icons
import CalendarIcon from "@material-ui/icons/CalendarToday";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import DescriptionIcon from "@material-ui/icons/Description";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import FilterListIcon from "@material-ui/icons/FilterList";
import CloseIcon from "@material-ui/icons/Close";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";

class MyPatients extends React.Component {
  constructor(props) {
    super(props);
    this._isMounted = false;

    this.state = {
      // Patient list state
      patients: [],
      selectedPatient: null,
      loading: false,
      loadingMore: false,
      hasMore: true,
      currentPage: 0,
      perPage: DEFAULT_PER_PAGE_RECORDS || 20,
      totalPatients: 0,

      // Filter state
      searchKeyword: "",
      filterDOB: null,
      filterCreatedOn: null,
      filterGender: "",
      filterInsurance: "",

      // Modal state
      createModalOpen: false,
      editModalOpen: false,
      editPatientData: null,

      // Patient details state (from getById API)
      selectedPatientDetails: null,
      loadingDetails: false,

      // Content Viewer State (like Referral tab)
      contentViewerOpen: false,
      viewerContent: "",
      viewerContentType: "",
      viewerFileName: "",
      loadingViewer: false,

      // Mobile responsive state
      mobilePatientPopupOpen: false, // First popup - patient details
      mobileContentViewerOpen: false, // Second popup - AI Insights/Documents
      mobileFiltersOpen: false, // Filter sidenav

      // Drop zone state
      dropZoneDragActive: false,
      processingUploads: [], // Array of {id, fileName, status: 'processing'|'completed'|'error'}
      pendingDropFiles: [], // Files waiting to be uploaded via Create button

      // Smart polling state
      pollingInterval: null, // Reference to setInterval for PROCESSING status polling
    };

    this.fetchPatients = this.fetchPatients.bind(this);
    this.handleSearch = this.handleSearch.bind(this);
    this.handleFilterChange = this.handleFilterChange.bind(this);
    this.handleDateFilterChange = this.handleDateFilterChange.bind(this);
    this.handlePatientSelect = this.handlePatientSelect.bind(this);
    this.handleCreateModalOpen = this.handleCreateModalOpen.bind(this);
    this.handleCreateModalClose = this.handleCreateModalClose.bind(this);
    this.handleEditModalOpen = this.handleEditModalOpen.bind(this);
    this.handleEditModalClose = this.handleEditModalClose.bind(this);
    this.handlePatientCreated = this.handlePatientCreated.bind(this);
    this.handlePatientUpdated = this.handlePatientUpdated.bind(this);
    this.handlePatientDeleted = this.handlePatientDeleted.bind(this);
    this.loadMorePatients = this.loadMorePatients.bind(this);
    this.fetchPatientDetails = this.fetchPatientDetails.bind(this);
    this.handleRefreshAll = this.handleRefreshAll.bind(this);
    this.handleRefreshDetails = this.handleRefreshDetails.bind(this);
    this.openContentViewer = this.openContentViewer.bind(this);
    this.closeContentViewer = this.closeContentViewer.bind(this);
    this.closeMobilePatientPopup = this.closeMobilePatientPopup.bind(this);
    this.closeMobileContentViewer = this.closeMobileContentViewer.bind(this);
    this.toggleMobileFilters = this.toggleMobileFilters.bind(this);
    this.handleDropZoneDrag = this.handleDropZoneDrag.bind(this);
    this.handleDropZoneDrop = this.handleDropZoneDrop.bind(this);
    this.handleDropZoneClick = this.handleDropZoneClick.bind(this);
    this.handleFileInputChange = this.handleFileInputChange.bind(this);
    this.processDroppedFiles = this.processDroppedFiles.bind(this);
    this.handleCreateFromDroppedFiles = this.handleCreateFromDroppedFiles.bind(this);
    this.removeDroppedFile = this.removeDroppedFile.bind(this);
    this.startProcessingPolling = this.startProcessingPolling.bind(this);
    this.stopProcessingPolling = this.stopProcessingPolling.bind(this);
    this.checkProcessingPatients = this.checkProcessingPatients.bind(this);

    // Create ref for hidden file input
    this.fileInputRef = React.createRef();
  }

  componentDidMount() {
    this._isMounted = true;
    this.fetchPatients();
  }

  componentWillUnmount() {
    this._isMounted = false;
    // Cleanup polling interval
    this.stopProcessingPolling();
  }

  async fetchPatients(isLoadMore = false) {
    if (!isLoadMore) {
      this.setState({ loading: true });
    } else {
      this.setState({ loadingMore: true });
    }

    try {
      const {
        searchKeyword,
        filterDOB,
        filterCreatedOn,
        filterGender,
        filterInsurance,
        currentPage,
        perPage,
      } = this.state;

      let queryParams = new URLSearchParams();
      queryParams.append("page", isLoadMore ? currentPage : 0);
      queryParams.append("size", perPage);
      queryParams.append("sort", "createdAt,desc");

      if (searchKeyword) queryParams.append("name", searchKeyword);
      if (filterDOB)
        queryParams.append(
          "dateOfBirth",
          moment(filterDOB).format("YYYY-MM-DD")
        );
      if (filterCreatedOn)
        queryParams.append(
          "createdOn",
          moment(filterCreatedOn).format("YYYY-MM-DD")
        );
      if (filterGender) queryParams.append("gender", filterGender);
      if (filterInsurance) queryParams.append("insurance", filterInsurance);

      const response = await fetch(
        `${apiPath.externalPatients}?${queryParams.toString()}`,
        {
          method: "GET",
          headers: generalAuthTokenHeader(),
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (this._isMounted) {
          const newPatients = data.content || [];
          const totalElements = data.totalElements || 0;
          const totalPages = data.totalPages || 0;
          const currentPageNum = data.number || 0;

          this.setState({
            patients: isLoadMore
              ? [...this.state.patients, ...newPatients]
              : newPatients,
            totalPatients: totalElements,
            hasMore: currentPageNum < totalPages - 1,
            currentPage: currentPageNum,
            loading: false,
            loadingMore: false,
          }, () => {
            // Start polling if there are PROCESSING patients
            this.startProcessingPolling();
          });
        }
      } else {
        // Handle error - for now just set empty state
        if (this._isMounted) {
          this.setState({
            patients: [],
            totalPatients: 0,
            hasMore: false,
            loading: false,
            loadingMore: false,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching patients:", error);
      userService.showNotification({
        title: "Error",
        message: "Failed to load patients",
        type: "danger",
      });
      if (this._isMounted) {
        this.setState({
          patients: [],
          loading: false,
          loadingMore: false,
        });
      }
    }
  }

  loadMorePatients() {
    if (!this.state.loadingMore && this.state.hasMore) {
      this.setState({ currentPage: this.state.currentPage + 1 }, () =>
        this.fetchPatients(true)
      );
    }
  }

  async fetchPatientDetails(patientId) {
    if (!patientId) return;

    this.setState({ loadingDetails: true });

    try {
      const response = await fetch(`${apiPath.externalPatients}/${patientId}`, {
        method: "GET",
        headers: generalAuthTokenHeader(),
      });

      if (response.ok) {
        const data = await response.json();
        if (this._isMounted) {
          this.setState({
            selectedPatientDetails: data,
            loadingDetails: false,
          });
        }
      } else {
        if (this._isMounted) {
          this.setState({ loadingDetails: false });
        }
      }
    } catch (error) {
      console.error("Error fetching patient details:", error);
      userService.showNotification({
        title: "Error",
        message: "Failed to load patient details",
        type: "danger",
      });
      if (this._isMounted) {
        this.setState({ loadingDetails: false });
      }
    }
  }

  handleRefreshAll() {
    this.fetchPatients();
    const { selectedPatient } = this.state;
    if (selectedPatient) {
      this.fetchPatientDetails(selectedPatient.id);
    }
  }

  handleRefreshDetails() {
    // Only refresh patient details without reloading the entire patient list
    const { selectedPatient } = this.state;
    if (selectedPatient) {
      this.fetchPatientDetails(selectedPatient.id);
    }
  }

  /**
   * Open content viewer with specified content
   */
  openContentViewer(content, contentType, fileName = "") {
    this.setState({
      contentViewerOpen: true,
      viewerContent: content,
      viewerContentType: contentType,
      viewerFileName: fileName,
      // Open mobile content viewer on mobile devices
      mobileContentViewerOpen: window.innerWidth <= 991,
    });
  }

  /**
   * Close content viewer
   */
  closeContentViewer() {
    this.setState({
      contentViewerOpen: false,
      viewerContent: "",
      viewerContentType: "",
      viewerFileName: "",
      mobileContentViewerOpen: false,
    });
  }

  handleSearch(event) {
    const value = event.target.value;
    this.setState({ searchKeyword: value, currentPage: 0 }, () => {
      // Debounce search
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => this.fetchPatients(), 500);
    });
  }

  handleFilterChange(event) {
    const { name, value } = event.target;
    this.setState({ [name]: value, currentPage: 0 }, () =>
      this.fetchPatients()
    );
  }

  handleDateFilterChange(name, date) {
    this.setState({ [name]: date, currentPage: 0 }, () => this.fetchPatients());
  }

  handlePatientSelect(patient) {
    // PROCESSING, FAILED, and temporary patients are non-clickable
    if (patient.status === "PROCESSING" || patient.status === "FAILED" || patient.isTemporary) {
      return;
    }
    // If patient is DRAFT, open edit modal
    if (patient.status === "DRAFT") {
      this.handleEditModalOpen(patient);
    } else {
      this.setState({
        selectedPatient: patient,
        selectedPatientDetails: null,
        // Show mobile popup when on mobile/tablet device (991px and below)
        mobilePatientPopupOpen: window.innerWidth <= 991,
        // Reset content viewer states when selecting new patient
        mobileContentViewerOpen: false,
        contentViewerOpen: false,
        viewerContent: "",
        viewerContentType: "",
        viewerFileName: "",
      });
      this.fetchPatientDetails(patient.id);
    }
  }

  /**
   * Close mobile patient popup (first popup)
   */
  closeMobilePatientPopup() {
    this.setState({
      mobilePatientPopupOpen: false,
      mobileContentViewerOpen: false, // Also close nested popup if open
    });
  }

  /**
   * Close mobile content viewer popup (second/nested popup)
   */
  closeMobileContentViewer() {
    this.setState({
      mobileContentViewerOpen: false,
      contentViewerOpen: false,
    });
  }

  /**
   * Toggle mobile filters sidenav
   */
  toggleMobileFilters() {
    this.setState((prevState) => ({
      mobileFiltersOpen: !prevState.mobileFiltersOpen,
    }));
  }

  /**
   * Handle drag events for the drop zone
   */
  handleDropZoneDrag(e) {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      this.setState({ dropZoneDragActive: true });
    } else if (e.type === "dragleave") {
      // Only set to false if leaving the drop zone entirely
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX;
      const y = e.clientY;
      if (
        x < rect.left ||
        x >= rect.right ||
        y < rect.top ||
        y >= rect.bottom
      ) {
        this.setState({ dropZoneDragActive: false });
      }
    }
  }

  /**
   * Handle file drop on the drop zone
   */
  handleDropZoneDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ dropZoneDragActive: false });

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      this.processDroppedFiles(e.dataTransfer.files);
    }
  }

  /**
   * Handle click on drop zone to trigger file input
   */
  handleDropZoneClick() {
    if (this.fileInputRef && this.fileInputRef.current) {
      this.fileInputRef.current.click();
    }
  }

  /**
   * Handle file selection from file input
   */
  handleFileInputChange(e) {
    if (e.target.files && e.target.files.length > 0) {
      this.processDroppedFiles(e.target.files);
      // Reset input so same file can be selected again
      e.target.value = "";
    }
  }

  /**
   * Process dropped files - validate and add to pending files list
   */
  processDroppedFiles(fileList) {
    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "image/gif",
      "image/webp",
    ];
    const maxSize = 10 * 1024 * 1024; // 10MB

    const validFiles = [];

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];

      if (!allowedTypes.includes(file.type)) {
        userService.showNotification({
          title: "Invalid File",
          message: `${file.name}: Only JPG, PNG, or PDF allowed.`,
          type: "warning",
        });
        continue;
      }

      if (file.size > maxSize) {
        userService.showNotification({
          title: "File Too Large",
          message: `${file.name}: File size exceeds 10MB limit.`,
          type: "warning",
        });
        continue;
      }

      validFiles.push(file);
    }

    if (validFiles.length === 0) {
      return;
    }

    // Add to pending files instead of uploading immediately
    this.setState((prevState) => ({
      pendingDropFiles: [...prevState.pendingDropFiles, ...validFiles],
    }));
  }

  /**
   * Remove a file from the pending drop files list
   */
  removeDroppedFile(index) {
    this.setState((prevState) => ({
      pendingDropFiles: prevState.pendingDropFiles.filter((_, i) => i !== index),
    }));
  }

  /**
   * Upload all pending files via AI Generated endpoint
   * Uses optimistic UI pattern - shows temp card immediately before API call
   */
  async handleCreateFromDroppedFiles() {
    const { pendingDropFiles } = this.state;
    if (pendingDropFiles.length === 0) return;

    // 1. Generate temporary ID
    const tempId = `temp-${Date.now()}`;

    // 2. Create temporary patient object IMMEDIATELY
    const tempPatient = {
      id: tempId,
      firstName: null,
      lastName: null,
      status: "PROCESSING",
      type: "AI_GENERATED",
      isTemporary: true,
      createdAt: new Date().toISOString(),
      fileCount: pendingDropFiles.length,
    };

    // 3. Add temp patient to list IMMEDIATELY (before API call) and clear pending files
    this.setState((prevState) => ({
      patients: [tempPatient, ...prevState.patients],
      pendingDropFiles: [],
      totalPatients: prevState.totalPatients + 1,
    }));

    try {
      // 4. Make API call in background
      const formData = new FormData();
      pendingDropFiles.forEach((file) => {
        formData.append("files", file);
      });

      const headers = multimediaAuthTokenHeader();
      delete headers["Content-Type"]; // Let browser set content type for FormData

      const response = await fetch(apiPath.externalPatientsAiGenerated, {
        method: "POST",
        headers: headers,
        body: formData,
      });

      if (response.ok || response.status === 201) {
        // API returns the patient with PROCESSING status
        const realPatient = await response.json();

        // 5. Replace temporary patient with real one
        this.setState((prevState) => ({
          patients: prevState.patients.map((p) =>
            p.id === tempId ? realPatient : p
          ),
        }), () => {
          // Start polling since we now have a real PROCESSING patient
          this.startProcessingPolling();
        });

        userService.showNotification({
          title: "Processing",
          message: "AI is processing your documents. The patient card will update when complete.",
          type: "info",
        });
      } else {
        // 6. Remove temporary patient on failure
        this.setState((prevState) => ({
          patients: prevState.patients.filter((p) => p.id !== tempId),
          totalPatients: prevState.totalPatients - 1,
        }));

        const errorData = await response.json().catch(() => ({}));
        userService.showNotification({
          title: "Error",
          message: errorData.message || "Failed to upload files",
          type: "danger",
        });
      }
    } catch (error) {
      // 7. Remove temporary patient on error
      this.setState((prevState) => ({
        patients: prevState.patients.filter((p) => p.id !== tempId),
        totalPatients: prevState.totalPatients - 1,
      }));

      console.error("Error uploading files:", error);
      userService.showNotification({
        title: "Error",
        message: "Failed to upload files",
        type: "danger",
      });
    }
  }

  handleCreateModalOpen() {
    this.setState({ createModalOpen: true });
  }

  handleCreateModalClose() {
    this.setState({ createModalOpen: false });
  }

  handleEditModalOpen(patient) {
    this.setState({ editModalOpen: true, editPatientData: patient });
  }

  handleEditModalClose() {
    this.setState({ editModalOpen: false, editPatientData: null });
  }

  handlePatientCreated(newPatient) {
    this.setState({ createModalOpen: false });
    this.fetchPatients();
  }

  handlePatientUpdated(updatedPatient) {
    this.setState({
      editModalOpen: false,
      editPatientData: null,
      selectedPatient: updatedPatient,
    });
    this.fetchPatients();
    if (updatedPatient && updatedPatient.id) {
      this.fetchPatientDetails(updatedPatient.id);
    }
  }

  handlePatientDeleted(patientId) {
    this.setState({ selectedPatient: null, selectedPatientDetails: null });
    this.fetchPatients();
  }

  /**
   * Start polling for PROCESSING patient status changes
   * Only starts if there are PROCESSING patients and no existing interval
   */
  startProcessingPolling() {
    const { patients, pollingInterval } = this.state;

    // Check if any patients have PROCESSING status (exclude temporary patients)
    const hasProcessingPatients = patients.some(p => p.status === "PROCESSING" && !p.isTemporary);

    if (hasProcessingPatients && !pollingInterval) {
      // Start polling every 8 seconds (consistent with notification polling pattern)
      const interval = setInterval(() => {
        this.checkProcessingPatients();
      }, 8000);

      this.setState({ pollingInterval: interval });
    } else if (!hasProcessingPatients && pollingInterval) {
      // No more PROCESSING patients, stop polling
      this.stopProcessingPolling();
    }
  }

  /**
   * Stop polling for PROCESSING patients
   */
  stopProcessingPolling() {
    const { pollingInterval } = this.state;
    if (pollingInterval) {
      clearInterval(pollingInterval);
      this.setState({ pollingInterval: null });
    }
  }

  /**
   * Check for status changes on PROCESSING patients
   * Called by the polling interval
   */
  async checkProcessingPatients() {
    if (!this._isMounted) return;

    const { patients } = this.state;

    // Get current PROCESSING patient IDs (exclude temporary patients that are still uploading)
    const processingPatients = patients.filter(p => p.status === "PROCESSING" && !p.isTemporary);

    if (processingPatients.length === 0) {
      this.stopProcessingPolling();
      return;
    }

    try {
      // Fetch fresh patient list to check for status changes
      const queryParams = new URLSearchParams();
      queryParams.append("page", 0);
      queryParams.append("size", patients.length > 20 ? patients.length : 20);
      queryParams.append("sort", "createdAt,desc");

      const response = await fetch(
        `${apiPath.externalPatients}?${queryParams.toString()}`,
        {
          method: "GET",
          headers: generalAuthTokenHeader(),
        }
      );

      if (response.ok && this._isMounted) {
        const data = await response.json();
        const freshPatients = data.content || [];

        // Check for status changes in previously PROCESSING patients
        let hasStatusChanges = false;
        const updatedPatients = patients.map(currentPatient => {
          if (currentPatient.status === "PROCESSING") {
            // Find the fresh version of this patient
            const freshPatient = freshPatients.find(fp => fp.id === currentPatient.id);

            if (freshPatient && freshPatient.status !== "PROCESSING") {
              hasStatusChanges = true;

              // Show notification based on new status
              if (freshPatient.status === "DRAFT") {
                userService.showNotification({
                  title: "Ready for Review",
                  message: "Patient is ready for review",
                  type: "success",
                });
              } else if (freshPatient.status === "COMPLETED") {
                userService.showNotification({
                  title: "Completed",
                  message: "Patient processing completed",
                  type: "success",
                });
              } else if (freshPatient.status === "FAILED") {
                userService.showNotification({
                  title: "Processing Failed",
                  message: "Patient processing failed",
                  type: "danger",
                });
              }

              return freshPatient;
            }
          }
          return currentPatient;
        });

        if (hasStatusChanges) {
          this.setState({ patients: updatedPatients }, () => {
            // Check if we should continue polling
            this.startProcessingPolling();
          });
        }
      }
    } catch (error) {
      // Log error but continue polling (don't break the interval)
      console.error("Error checking processing patients:", error);
    }
  }

  formatDate(dateString) {
    if (!dateString) return "";
    return moment(dateString).format("MM/DD/YYYY");
  }

  render() {
    const {
      patients,
      selectedPatient,
      selectedPatientDetails,
      loading,
      loadingDetails,
      loadingMore,
      hasMore,
      totalPatients,
      searchKeyword,
      filterDOB,
      filterCreatedOn,
      filterGender,
      filterInsurance,
      createModalOpen,
      editModalOpen,
      editPatientData,
      contentViewerOpen,
      mobilePatientPopupOpen,
      mobileContentViewerOpen,
      mobileFiltersOpen,
      dropZoneDragActive,
      processingUploads,
      pendingDropFiles,
    } = this.state;

    return (
      <div
        className={`MyPatients-page${
          mobileContentViewerOpen ? " content-viewer-active" : ""
        }`}
        style={{ padding: "24px" }}
      >
        {/* Linear Progress Loader - Same as Referral tab */}
        {loading ? (
          <div className="LinearProgress-ref">
            <LinearProgress />
          </div>
        ) : (
          <div className="LinearProgress-ref"></div>
        )}

        {/* Patient Tabs Navigation */}
        <PatientTabs activeTab="myPatients" />

        <div className="left-content-header content-header">
          <Box
            display="flex"
            flexDirection="row"
            className="filters-row desktop-filters"
            alignItems={"center"}
          >
            {/* My Patients Title */}
            <Box marginRight={"30px"}>
              <span className="titlename">My Intake</span>
            </Box>

            {/* Total Patient Count */}
            <Box minWidth="80px">
              <span className="show-text">
                {patients.length}/{totalPatients} Patient
                {totalPatients !== 1 ? "s" : ""}
              </span>
            </Box>

            {/* Search Box */}
            <Box
              marginRight={"15px"}
              minWidth="150px"
              className="search-box-wrapper"
            >
              <Paper component="form" className="search-box-cover filter-box">
                <InputBase
                  className="search-box"
                  placeholder="Search by Name and Facility"
                  value={searchKeyword}
                  onChange={this.handleSearch}
                  onKeyPress={(e) => e.key === "Enter" && e.preventDefault()}
                />
                <IconButton aria-label="search" style={{ padding: "5px" }}>
                  <SearchIcon />
                </IconButton>
              </Paper>
            </Box>

            {/* Desktop Filter Dropdowns Container */}
            <Box
              className="filter-dropdowns-container"
              display="flex"
              flexDirection="row"
              flexWrap="wrap"
              alignItems="center"
            >
              {/* Date of Birth Filter */}
              <Box className="box-filter-ref filter-1" minWidth="150px">
                <FormGroup>
                  <FormControl variant="outlined" size="small">
                    <InputLabel
                      shrink={false}
                      style={{
                        position: "static",
                        transform: "none",
                        marginBottom: "6px",
                        color: "#2A2A2A !important",
                        fontSize: "11px",
                        fontWeight: 500,
                      }}
                    >
                      Date of Birth
                    </InputLabel>
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                      <KeyboardDatePicker
                        format="MM/dd/yyyy"
                        value={filterDOB}
                        onChange={(date) =>
                          this.handleDateFilterChange("filterDOB", date)
                        }
                        inputVariant="outlined"
                        placeholder="DOB"
                        size="small"
                        className="filter-date-picker"
                        KeyboardButtonProps={{ "aria-label": "select DOB" }}
                        clearable
                        autoOk
                      />
                    </MuiPickersUtilsProvider>
                  </FormControl>
                </FormGroup>
              </Box>

              {/* Created On Filter */}
              <Box className="box-filter-ref filter-2" minWidth="150px">
                <FormGroup>
                  <FormControl variant="outlined" size="small">
                    <InputLabel
                      shrink={false}
                      style={{
                        position: "static",
                        transform: "none",
                        marginBottom: "6px",
                        color: "#2A2A2A",
                        fontSize: "11px",
                        fontWeight: 500,
                      }}
                    >
                      Created on
                    </InputLabel>
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                      <KeyboardDatePicker
                        format="MM/dd/yyyy"
                        value={filterCreatedOn}
                        onChange={(date) =>
                          this.handleDateFilterChange("filterCreatedOn", date)
                        }
                        inputVariant="outlined"
                        placeholder="Created Date"
                        size="small"
                        className="filter-date-picker"
                        KeyboardButtonProps={{
                          "aria-label": "select created date",
                        }}
                        clearable
                        autoOk
                      />
                    </MuiPickersUtilsProvider>
                  </FormControl>
                </FormGroup>
              </Box>

              {/* Gender Filter */}
              <Box className="box-filter-ref filter-3" minWidth="150px">
                <FormGroup>
                  <FormControl variant="outlined" size="small">
                    <InputLabel
                      shrink={false}
                      style={{
                        position: "static",
                        transform: "none",
                        marginBottom: "6px",
                        color: "#2A2A2A",
                        fontSize: "11px",
                        fontWeight: 500,
                      }}
                    >
                      Gender
                    </InputLabel>
                    <Select
                      id="filterGender"
                      name="filterGender"
                      className="filter-select filter-box"
                      value={filterGender}
                      onChange={this.handleFilterChange}
                      displayEmpty
                    >
                      <MenuItem value="">Select Gender</MenuItem>
                      <MenuItem value="Male">Male</MenuItem>
                      <MenuItem value="Female">Female</MenuItem>
                      <MenuItem value="Others">Others</MenuItem>
                    </Select>
                  </FormControl>
                </FormGroup>
              </Box>

              {/* Insurance Filter */}
              <Box className="box-filter-ref filter-4" minWidth="200px">
                <FormGroup>
                  <FormControl variant="outlined" size="small">
                    <InputLabel
                      shrink={false}
                      style={{
                        position: "static",
                        transform: "none",
                        marginBottom: "6px",
                        color: "#2A2A2A",
                        fontSize: "11px",
                        fontWeight: 500,
                      }}
                    >
                      Insurance Provider
                    </InputLabel>
                    <TextField
                      id="filterInsurance"
                      name="filterInsurance"
                      className="filter-text-field filter-box"
                      value={filterInsurance}
                      onChange={this.handleFilterChange}
                      variant="outlined"
                      size="small"
                      placeholder="Search Insurance"
                    />
                  </FormControl>
                </FormGroup>
              </Box>
            </Box>
          </Box>
          {/* Create Patient Button */}
          <Box marginLeft="auto">
            <Button
              className="create-patient-btn"
              onClick={this.handleCreateModalOpen}
            >
              CREATE INTAKE
            </Button>
          </Box>
        </div>

        {/* Mobile Filter Header - Shows on mobile only */}
        <div className="mobile-filter-header">
          <div className="mobile-filter-row-1">
            <span className="titlename">My Patients</span>
            <span className="show-text">
              {patients.length}/{totalPatients} Patient
              {totalPatients !== 1 ? "s" : ""}
            </span>
          </div>
          <div className="mobile-filter-row-2">
            <Box className="mobile-search-wrapper">
              <Paper component="form" className="search-box-cover filter-box">
                <InputBase
                  className="search-box"
                  placeholder="Search by Name"
                  value={searchKeyword}
                  onChange={this.handleSearch}
                  onKeyPress={(e) => e.key === "Enter" && e.preventDefault()}
                />
                <IconButton aria-label="search" style={{ padding: "5px" }}>
                  <SearchIcon />
                </IconButton>
              </Paper>
            </Box>
            <IconButton
              className="filter-icon-btn"
              onClick={this.toggleMobileFilters}
              aria-label="Open filters"
            >
              <FilterListIcon />
            </IconButton>
          </div>
        </div>

        {/* Mobile Filter Sidenav Overlay */}
        {mobileFiltersOpen && (
          <div
            className="mobile-filter-overlay"
            onClick={this.toggleMobileFilters}
          />
        )}

        {/* Mobile Filter Sidenav */}
        <div
          className={`mobile-filter-sidenav ${
            mobileFiltersOpen ? "sidenav-open" : ""
          }`}
        >
          <div className="sidenav-header">
            <span className="sidenav-title">Filters</span>
            <IconButton
              className="sidenav-close-btn"
              onClick={this.toggleMobileFilters}
            >
              <CloseIcon />
            </IconButton>
          </div>
          <div className="sidenav-content">
            {/* Date of Birth Filter */}
            <div className="sidenav-filter-item">
              <label>Date of Birth</label>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <KeyboardDatePicker
                  format="MM/dd/yyyy"
                  value={filterDOB}
                  onChange={(date) =>
                    this.handleDateFilterChange("filterDOB", date)
                  }
                  inputVariant="outlined"
                  placeholder="Select DOB"
                  size="small"
                  fullWidth
                  clearable
                  autoOk
                />
              </MuiPickersUtilsProvider>
            </div>

            {/* Created On Filter */}
            <div className="sidenav-filter-item">
              <label>Created On</label>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <KeyboardDatePicker
                  format="MM/dd/yyyy"
                  value={filterCreatedOn}
                  onChange={(date) =>
                    this.handleDateFilterChange("filterCreatedOn", date)
                  }
                  inputVariant="outlined"
                  placeholder="Select Date"
                  size="small"
                  fullWidth
                  clearable
                  autoOk
                />
              </MuiPickersUtilsProvider>
            </div>

            {/* Gender Filter */}
            <div className="sidenav-filter-item">
              <label>Gender</label>
              <FormControl variant="outlined" size="small" fullWidth>
                <Select
                  name="filterGender"
                  value={filterGender}
                  onChange={this.handleFilterChange}
                  displayEmpty
                >
                  <MenuItem value="">Select Gender</MenuItem>
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="Others">Others</MenuItem>
                </Select>
              </FormControl>
            </div>

            {/* Insurance Filter */}
            <div className="sidenav-filter-item">
              <label>Insurance Provider</label>
              <TextField
                name="filterInsurance"
                value={filterInsurance}
                onChange={this.handleFilterChange}
                variant="outlined"
                size="small"
                fullWidth
                placeholder="Search Insurance"
              />
            </div>

            {/* Create Patient Button in Mobile */}
            <Box mt={3}>
              <Button
                className="create-patient-btn"
                onClick={() => {
                  this.toggleMobileFilters();
                  this.handleCreateModalOpen();
                }}
                fullWidth
              >
                CREATE PATIENT
              </Button>
            </Box>
          </div>
        </div>

        <Box display="flex" width={1} className="my-patients-container">
          {/* Left Panel - Patient List */}
          <Box
            className="left-content-box-patients part-1"
            style={{
              width: this.state.contentViewerOpen ? "15%" : "50%",
              transition: "width 0.3s ease-in-out",
            }}
          >
            {/* Header with Filters */}

            {/* Quick Drop Zone for Patient Documents */}
            <div
              className={`patient-drop-zone ${dropZoneDragActive ? "drag-active" : ""} ${contentViewerOpen ? "compact" : ""} ${pendingDropFiles.length > 0 ? "has-files" : ""}`}
              onDragEnter={this.handleDropZoneDrag}
              onDragLeave={this.handleDropZoneDrag}
              onDragOver={this.handleDropZoneDrag}
              onDrop={this.handleDropZoneDrop}
              onClick={pendingDropFiles.length === 0 ? this.handleDropZoneClick : undefined}
            >
              <input
                ref={this.fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,image/*,application/pdf"
                multiple
                style={{ display: "none" }}
                onChange={this.handleFileInputChange}
              />

              {pendingDropFiles.length === 0 ? (
                <>
                  <CloudUploadIcon className="drop-zone-icon" />
                  <div className="drop-zone-text">
                    <span className="drop-zone-title">Drop files here to create intake</span>
                    <span className="drop-zone-subtitle">JPG, PNG or PDF, max 10MB</span>
                  </div>
                </>
              ) : (
                <div className="pending-files-container">
                  <div className="pending-files-list">
                    {pendingDropFiles.map((file, index) => (
                      <div key={index} className="pending-file-item">
                        <DescriptionIcon className="file-icon" />
                        <span className="file-name">{file.name}</span>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            this.removeDroppedFile(index);
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </div>
                    ))}
                  </div>
                  <div className="pending-files-actions">
                    <span className="drop-more-text">Drop more files or</span>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        this.handleCreateFromDroppedFiles();
                      }}
                      className="create-btn"
                    >
                      Create
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Processing Uploads Animation */}
            {processingUploads.length > 0 && (
              <div className="processing-uploads-container">
                {processingUploads.map((upload) => (
                  <div
                    key={upload.id}
                    className={`processing-upload-item ${upload.status}`}
                  >
                    <div className="processing-content">
                      {upload.status === "processing" && (
                        <>
                          <div className="magic-animation">
                            <span className="magic-sparkle">✨</span>
                            <span className="magic-sparkle">✨</span>
                            <span className="magic-sparkle">✨</span>
                          </div>
                          <span className="processing-text">
                            AI is working its magic...
                          </span>
                        </>
                      )}
                      {upload.status === "completed" && (
                        <>
                          <CheckCircleIcon className="status-icon completed" />
                          <span className="processing-text">
                            Document processed. Patient will appear in the list
                            soon.
                          </span>
                        </>
                      )}
                      {upload.status === "error" && (
                        <>
                          <CloseIcon className="status-icon error" />
                          <span className="processing-text">
                            Failed to process file
                          </span>
                        </>
                      )}
                    </div>
                    <span className="processing-filename">
                      {upload.fileName}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Patient List */}
            <div className="left-content-list patients-list">
              {loading ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="300px"
                >
                  <CircularProgress />
                </Box>
              ) : patients.length === 0 ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="300px"
                  className="no-patients-found"
                >
                  <div className="no-patients-text">
                    <span>No Patients Found</span>
                    <p>
                      Create your first patient by clicking the "CREATE INTAKE"
                      button
                    </p>
                  </div>
                </Box>
              ) : (
                <InfiniteScroll
                  pageStart={0}
                  loadMore={this.loadMorePatients}
                  hasMore={hasMore && !loadingMore}
                  loader={
                    <Box
                      display="flex"
                      justifyContent="center"
                      p={2}
                      key="loader"
                    >
                      <CircularProgress size={24} />
                    </Box>
                  }
                  useWindow={false}
                >
                  {patients.map((patient, index) => {
                    const isNonClickable = patient.status === "PROCESSING" || patient.status === "FAILED" || patient.isTemporary;
                    return (
                    <div
                      key={patient.id || index}
                      className={`patient-list-item ${
                        selectedPatient && selectedPatient.id === patient.id
                          ? "selected"
                          : ""
                      } ${contentViewerOpen ? "compact-view" : ""} ${isNonClickable ? "non-clickable" : ""}`}
                      onClick={() => this.handlePatientSelect(patient)}
                    >
                      {/* Vertical layout when content viewer is open (collapsed state) */}
                      {contentViewerOpen ? (
                        <div className="patient-layout-compact">
                          {/* For PROCESSING: Patient ID and centered text */}
                          {patient.status === "PROCESSING" ? (
                            <>
                              <div className="patient-name-compact">
                                {patient.isTemporary ? `Uploading ${patient.fileCount || ''} file(s)...` : `Patient ID: ${patient.id}`}
                              </div>
                              <div className="processing-center-text-compact">
                                {patient.isTemporary ? 'Please wait...' : 'AI is processing your documents...'}
                              </div>
                            </>
                          ) : (
                            <>
                              {/* Show facility name for DRAFT and COMPLETED with referringFacility */}
                              {(patient.status === "DRAFT" || patient.status === "COMPLETED") && patient.referringFacility && (
                                <div className="facility-name-compact">
                                  {patient.referringFacility}
                                </div>
                              )}
                              {/* Name or Patient ID for FAILED */}
                              <div className={
                                (patient.status === "DRAFT" || patient.status === "COMPLETED") && patient.referringFacility
                                  ? "patient-name-with-facility-compact"
                                  : "patient-name-compact"
                              }>
                                {patient.status === "FAILED"
                                  ? `Patient ID: ${patient.id}`
                                  : `${patient.lastName ? patient.lastName + ", " : ""}${patient.firstName || "N/A"}`
                                }
                              </div>

                              {/* Content area - different based on status */}
                              {patient.status === "FAILED" ? (
                                <div className="failed-placeholder"></div>
                              ) : (
                                <div className="patient-intake">
                                  Intake:{" "}
                                  {patient.type === "AI_GENERATED"
                                    ? "AI Generated"
                                    : "Manually"}
                                </div>
                              )}
                            </>
                          )}

                          {/* Status Badge */}
                          <div className="patient-status-compact">
                            <div
                              className={`status-badge ${patient.status?.toLowerCase() ||
                                "draft"}`}
                            >
                              {patient.status === "COMPLETED" ? (
                                <>
                                  <CheckCircleIcon className="status-icon" />
                                  Completed
                                </>
                              ) : patient.status === "PROCESSING" ? (
                                <>
                                  <div className="ai-animation">
                                    <span className="ai-sparkle">✨</span>
                                    <span className="ai-sparkle">✨</span>
                                    <span className="ai-sparkle">✨</span>
                                  </div>
                                  Processing
                                </>
                              ) : patient.status === "FAILED" ? (
                                <>
                                  <CloseIcon className="status-icon" />
                                  Failed
                                </>
                              ) : (
                                <>
                                  <DescriptionIcon className="status-icon" />
                                  Verify Draft
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        /* Horizontal layout when content viewer is closed (expanded state) - Figma Design */
                        <div className="patient-layout-horizontal">
                          {/* Left: Name/ID and Intake */}
                          <div className="patient-info-section">
                            {/* Show facility name for DRAFT and COMPLETED with referringFacility */}
                            {(patient.status === "DRAFT" || patient.status === "COMPLETED") && patient.referringFacility && (
                              <div className="facility-name">
                                {patient.referringFacility}
                              </div>
                            )}
                            <div className={
                              (patient.status === "DRAFT" || patient.status === "COMPLETED") && patient.referringFacility
                                ? "patient-name-with-facility"
                                : "patient-name"
                            }>
                              {patient.status === "PROCESSING"
                                ? (patient.isTemporary ? `Uploading ${patient.fileCount || ''} file(s)...` : `Patient ID: ${patient.id}`)
                                : patient.status === "FAILED"
                                ? `Patient ID: ${patient.id}`
                                : `${patient.firstName || ""} ${patient.lastName || "N/A"}`
                              }
                            </div>
                            {patient.status === "FAILED" ? (
                              <div className="failed-placeholder"></div>
                            ) : patient.status === "PROCESSING" ? (
                              <div className="processing-placeholder"></div>
                            ) : (
                              <div className="patient-intake">
                                Intake:{" "}
                                {patient.type === "AI_GENERATED"
                                  ? "AI Generated"
                                  : "Manually"}
                              </div>
                            )}
                          </div>

                          {/* Middle: Center text for PROCESSING or Details Grid for others */}
                          {patient.status === "PROCESSING" ? (
                            <div className="processing-center-section">
                              <span className="processing-center-text">
                                {patient.isTemporary ? 'Please wait...' : 'AI is processing your documents...'}
                              </span>
                            </div>
                          ) : !isNonClickable && (
                          <div className="patient-details-grid">
                            <div className="details-column">
                              <div className="detail-item">
                                <svg
                                  width="11"
                                  height="12"
                                  viewBox="0 0 11 12"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="detail-icon"
                                >
                                  <path
                                    d="M2.92857 0C3.33354 0 3.66071 0.318088 3.66071 0.711806V1.42361H6.58929V0.711806C6.58929 0.318088 6.91646 0 7.32143 0C7.7264 0 8.05357 0.318088 8.05357 0.711806V1.42361H9.15179C9.75809 1.42361 10.25 1.90186 10.25 2.49132V3.55903H0V2.49132C0 1.90186 0.491908 1.42361 1.09821 1.42361H2.19643V0.711806C2.19643 0.318088 2.5236 0 2.92857 0ZM0 4.27083H10.25V10.3212C10.25 10.9106 9.75809 11.3889 9.15179 11.3889H1.09821C0.491908 11.3889 0 10.9106 0 10.3212V4.27083ZM1.46429 6.05035V6.76215C1.46429 6.9579 1.62902 7.11806 1.83036 7.11806H2.5625C2.76384 7.11806 2.92857 6.9579 2.92857 6.76215V6.05035C2.92857 5.8546 2.76384 5.69444 2.5625 5.69444H1.83036C1.62902 5.69444 1.46429 5.8546 1.46429 6.05035ZM4.39286 6.05035V6.76215C4.39286 6.9579 4.55759 7.11806 4.75893 7.11806H5.49107C5.69241 7.11806 5.85714 6.9579 5.85714 6.76215V6.05035C5.85714 5.8546 5.69241 5.69444 5.49107 5.69444H4.75893C4.55759 5.69444 4.39286 5.8546 4.39286 6.05035ZM7.6875 5.69444C7.48616 5.69444 7.32143 5.8546 7.32143 6.05035V6.76215C7.32143 6.9579 7.48616 7.11806 7.6875 7.11806H8.41964C8.62098 7.11806 8.78571 6.9579 8.78571 6.76215V6.05035C8.78571 5.8546 8.62098 5.69444 8.41964 5.69444H7.6875ZM1.46429 8.89757V9.60938C1.46429 9.80512 1.62902 9.96528 1.83036 9.96528H2.5625C2.76384 9.96528 2.92857 9.80512 2.92857 9.60938V8.89757C2.92857 8.70182 2.76384 8.54167 2.5625 8.54167H1.83036C1.62902 8.54167 1.46429 8.70182 1.46429 8.89757ZM4.75893 8.54167C4.55759 8.54167 4.39286 8.70182 4.39286 8.89757V9.60938C4.39286 9.80512 4.55759 9.96528 4.75893 9.96528H5.49107C5.69241 9.96528 5.85714 9.80512 5.85714 9.60938V8.89757C5.85714 8.70182 5.69241 8.54167 5.49107 8.54167H4.75893ZM7.32143 8.89757V9.60938C7.32143 9.80512 7.48616 9.96528 7.6875 9.96528H8.41964C8.62098 9.96528 8.78571 9.80512 8.78571 9.60938V8.89757C8.78571 8.70182 8.62098 8.54167 8.41964 8.54167H7.6875C7.48616 8.54167 7.32143 8.70182 7.32143 8.89757Z"
                                    fill="black"
                                  />
                                </svg>
                                DOB: {this.formatDate(patient.dateOfBirth)}
                              </div>
                              <div className="detail-item">
                                <svg
                                  width="15"
                                  height="15"
                                  viewBox="0 0 15 15"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="detail-icon"
                                >
                                  <path
                                    d="M10.7207 0.0694284C10.6797 0.130952 10.6738 0.24228 10.6826 0.848725C10.6914 1.51083 10.6943 1.56064 10.7471 1.59873C10.7939 1.63095 10.9463 1.63974 11.4941 1.63974H12.1855L11.0928 2.73251L10 3.82529L9.77441 3.71396C8.58496 3.12802 7.17285 3.28916 6.12988 4.12412L6.01855 4.21201L6.17676 4.4083C6.37305 4.65439 6.55762 4.97373 6.68066 5.27841C6.73047 5.40439 6.78027 5.51572 6.78613 5.52451C6.79492 5.53037 6.88281 5.46005 6.98242 5.36337C7.375 4.98544 7.94336 4.79794 8.48828 4.86533C9.28809 4.96494 9.95898 5.58896 10.1055 6.36533C10.1611 6.6583 10.1611 6.80185 10.1025 7.0831C9.97363 7.72177 9.49902 8.26962 8.88086 8.48935C8.60254 8.58896 8.11328 8.6124 7.83203 8.54208C7.4541 8.4454 7.03516 8.18759 6.85352 7.9415C6.7627 7.81845 6.72168 7.79208 6.72168 7.85947C6.72168 7.92099 6.49902 8.3663 6.36133 8.57724C6.29102 8.68564 6.16504 8.85849 6.07715 8.96103L5.92188 9.14853L6.03613 9.24521C6.53418 9.67294 7.14941 9.95419 7.80566 10.0509C8.16895 10.1036 8.74902 10.0714 9.10645 9.97763C9.47559 9.88095 9.98242 9.63779 10.2812 9.4122C11.582 8.4249 11.9893 6.7081 11.2744 5.22275L11.1689 5.00009L12.2559 3.91318L13.3428 2.82626V3.50302C13.3428 4.03916 13.3516 4.18857 13.3838 4.23544C13.4219 4.28818 13.4717 4.29111 14.125 4.2999C14.5557 4.30576 14.8574 4.29697 14.9043 4.27939L14.9834 4.24716L14.9775 2.13193L14.9687 0.0137644L12.8682 0.00497532L10.7676 -0.000884056L10.7207 0.0694284Z"
                                    fill="black"
                                  />
                                  <path
                                    d="M3.10547 3.27222C1.63184 3.3894 0.457031 4.35913 0.0673828 5.7771C0.00878906 5.9939 0 6.08472 0 6.62085C0 7.29761 0.0322266 7.47339 0.243164 7.96558C0.621094 8.83569 1.38867 9.53003 2.29395 9.81714L2.50488 9.88452V11.2087V12.5359L1.87207 12.5447C1.28613 12.5535 1.23633 12.5564 1.19824 12.6091C1.13672 12.6941 1.13672 14.0535 1.19824 14.1384C1.23633 14.1912 1.28613 14.1941 1.87207 14.2029L2.50488 14.2117V14.572C2.50488 14.8093 2.5166 14.9412 2.54004 14.9646C2.58984 15.0144 4.06055 15.0144 4.11035 14.9646C4.13379 14.9412 4.14551 14.8093 4.14551 14.572V14.2117L4.77832 14.2029C5.36426 14.1941 5.41406 14.1912 5.45215 14.1384C5.51367 14.0535 5.51367 12.6941 5.45215 12.6091C5.41406 12.5564 5.36426 12.5535 4.77832 12.5447L4.14551 12.5359V11.2087V9.88452L4.35938 9.81714C5.63379 9.40991 6.52441 8.29663 6.66797 6.94311C6.74121 6.24585 6.57422 5.51636 6.20508 4.88647C5.76855 4.15405 5.00098 3.58276 4.1543 3.3689C3.95508 3.31616 3.37793 3.24292 3.2666 3.25757C3.24316 3.2605 3.16992 3.26636 3.10547 3.27222ZM3.88184 4.83081C4.77832 5.11792 5.34082 6.06421 5.15918 6.97241C4.90137 8.2439 3.49805 8.88843 2.3877 8.24097C2.15625 8.1062 1.85156 7.80444 1.71973 7.58179C1.08398 6.50659 1.64941 5.12964 2.8623 4.80444C3.14355 4.72827 3.60352 4.73999 3.88184 4.83081Z"
                                    fill="black"
                                  />
                                </svg>
                                Gender: {patient.gender || "N/A"}
                              </div>
                            </div>
                            <div className="details-column">
                              <div className="detail-item">
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 14 14"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="detail-icon"
                                >
                                  <path
                                    d="M3.41316 0.0223923C3.29285 0.0716114 3.16707 0.183721 3.10418 0.301299C3.04675 0.413408 3.04402 0.446221 3.03582 1.26927L3.02761 2.12239L1.70964 2.1306L0.391676 2.1388L0.28777 2.20989C0.142848 2.31106 0.0580821 2.43685 0.0225352 2.60091C0.00339455 2.69661 -0.0020742 4.42474 0.000660178 8.15442L0.0088633 13.5685L0.0772227 13.6915C0.115504 13.7572 0.20027 13.8501 0.26316 13.8966L0.383473 13.9786H6.96785H13.5522L13.6725 13.8966C13.7354 13.8501 13.8202 13.7572 13.8585 13.6915L13.9268 13.5685L13.935 8.15442C13.9378 4.42474 13.9323 2.69661 13.9132 2.60091C13.8776 2.43685 13.7928 2.31106 13.6479 2.20989L13.544 2.1388L12.2261 2.1306L10.9081 2.12239L10.8999 1.26927C10.8917 0.416143 10.8917 0.416142 10.8233 0.293096C10.785 0.22747 10.7003 0.134501 10.6374 0.0880175L10.5171 0.00598621L7.00066 0.000517845C4.72566 -0.00221634 3.45691 0.00598621 3.41316 0.0223923ZM9.70222 1.66028V2.12513H6.96785H4.23347V1.66028V1.19544H6.96785H9.70222V1.66028Z"
                                    fill="black"
                                  />
                                  <path
                                    d="M6.39732 8.56374H3.54018C3.37828 8.56374 3.24266 8.50889 3.13332 8.39917C3.02399 8.28946 2.96913 8.15384 2.96875 7.99231C2.96837 7.83079 3.02323 7.69517 3.13332 7.58546C3.24342 7.47574 3.37904 7.42089 3.54018 7.42089H6.39732V4.56374C6.39732 4.40184 6.45218 4.26622 6.56189 4.15689C6.67161 4.04755 6.80723 3.9927 6.96875 3.99232C7.13027 3.99194 7.26608 4.04679 7.37618 4.15689C7.48627 4.26698 7.54094 4.4026 7.54018 4.56374V7.42089H10.3973C10.5592 7.42089 10.695 7.47574 10.8047 7.58546C10.9145 7.69517 10.9691 7.83079 10.9687 7.99231C10.9684 8.15384 10.9135 8.28965 10.8042 8.39974C10.6948 8.50984 10.5592 8.5645 10.3973 8.56374H7.54018V11.4209C7.54018 11.5828 7.48532 11.7186 7.37561 11.8283C7.26589 11.938 7.13027 11.9927 6.96875 11.9923C6.80723 11.9919 6.67161 11.9371 6.56189 11.8277C6.45218 11.7184 6.39732 11.5828 6.39732 11.4209V8.56374Z"
                                    fill="white"
                                  />
                                </svg>
                                Insurance: {patient.insurance || "N/A"}
                              </div>
                              <div className="detail-item">
                                <svg
                                  width="11"
                                  height="12"
                                  viewBox="0 0 11 12"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="detail-icon"
                                >
                                  <path
                                    d="M2.92857 0C3.33354 0 3.66071 0.318088 3.66071 0.711806V1.42361H6.58929V0.711806C6.58929 0.318088 6.91646 0 7.32143 0C7.7264 0 8.05357 0.318088 8.05357 0.711806V1.42361H9.15179C9.75809 1.42361 10.25 1.90186 10.25 2.49132V3.55903H0V2.49132C0 1.90186 0.491908 1.42361 1.09821 1.42361H2.19643V0.711806C2.19643 0.318088 2.5236 0 2.92857 0ZM0 4.27083H10.25V10.3212C10.25 10.9106 9.75809 11.3889 9.15179 11.3889H1.09821C0.491908 11.3889 0 10.9106 0 10.3212V4.27083ZM1.46429 6.05035V6.76215C1.46429 6.9579 1.62902 7.11806 1.83036 7.11806H2.5625C2.76384 7.11806 2.92857 6.9579 2.92857 6.76215V6.05035C2.92857 5.8546 2.76384 5.69444 2.5625 5.69444H1.83036C1.62902 5.69444 1.46429 5.8546 1.46429 6.05035ZM4.39286 6.05035V6.76215C4.39286 6.9579 4.55759 7.11806 4.75893 7.11806H5.49107C5.69241 7.11806 5.85714 6.9579 5.85714 6.76215V6.05035C5.85714 5.8546 5.69241 5.69444 5.49107 5.69444H4.75893C4.55759 5.69444 4.39286 5.8546 4.39286 6.05035ZM7.6875 5.69444C7.48616 5.69444 7.32143 5.8546 7.32143 6.05035V6.76215C7.32143 6.9579 7.48616 7.11806 7.6875 7.11806H8.41964C8.62098 7.11806 8.78571 6.9579 8.78571 6.76215V6.05035C8.78571 5.8546 8.62098 5.69444 8.41964 5.69444H7.6875ZM1.46429 8.89757V9.60938C1.46429 9.80512 1.62902 9.96528 1.83036 9.96528H2.5625C2.76384 9.96528 2.92857 9.80512 2.92857 9.60938V8.89757C2.92857 8.70182 2.76384 8.54167 2.5625 8.54167H1.83036C1.62902 8.54167 1.46429 8.70182 1.46429 8.89757ZM4.75893 8.54167C4.55759 8.54167 4.39286 8.70182 4.39286 8.89757V9.60938C4.39286 9.80512 4.55759 9.96528 4.75893 9.96528H5.49107C5.69241 9.96528 5.85714 9.80512 5.85714 9.60938V8.89757C5.85714 8.70182 5.69241 8.54167 5.49107 8.54167H4.75893ZM7.32143 8.89757V9.60938C7.32143 9.80512 7.48616 9.96528 7.6875 9.96528H8.41964C8.62098 9.96528 8.78571 9.80512 8.78571 9.60938V8.89757C8.78571 8.70182 8.62098 8.54167 8.41964 8.54167H7.6875C7.48616 8.54167 7.32143 8.70182 7.32143 8.89757Z"
                                    fill="black"
                                  />
                                </svg>
                                Date created:{" "}
                                {this.formatDate(patient.createdAt)}
                              </div>
                            </div>
                          </div>
                          )}

                          {/* Right: Status Badge */}
                          <div className="patient-status-section">
                            <div
                              className={`status-badge ${patient.status?.toLowerCase() ||
                                "draft"}`}
                            >
                              {patient.status === "COMPLETED" ? (
                                <>
                                  <CheckCircleIcon className="status-icon" />
                                  Completed
                                </>
                              ) : patient.status === "PROCESSING" ? (
                                <>
                                  <div className="ai-animation">
                                    <span className="ai-sparkle">✨</span>
                                    <span className="ai-sparkle">✨</span>
                                    <span className="ai-sparkle">✨</span>
                                  </div>
                                  Processing
                                </>
                              ) : patient.status === "FAILED" ? (
                                <>
                                  <CloseIcon className="status-icon" />
                                  Failed
                                </>
                              ) : (
                                <>
                                  <DescriptionIcon className="status-icon" />
                                  Verify Draft
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );})}
                </InfiniteScroll>
              )}
            </div>
          </Box>

          {/* Right Panel - Patient Details */}
          <Box
            className={`left-content-box-patients part-2${
              mobilePatientPopupOpen ? " mobile-popup-active" : ""
            }`}
            style={{
              width: this.state.contentViewerOpen ? "85%" : "50%",
              transition: "width 0.3s ease-in-out",
            }}
          >
            {/* Mobile popup close button */}
            {mobilePatientPopupOpen && (
              <button
                className="mobile-popup-close"
                onClick={this.closeMobilePatientPopup}
                aria-label="Close patient details"
              >
                <ArrowBackIcon style={{ fontSize: 20, color: "#333" }} />
              </button>
            )}
            {/* Mobile popup overlay - click to close */}
            {mobilePatientPopupOpen && (
              <div
                className="mobile-popup-overlay"
                onClick={this.closeMobilePatientPopup}
              />
            )}
            {/* Content viewer overlay for mobile - clicking closes content viewer */}
            {mobilePatientPopupOpen && mobileContentViewerOpen && (
              <div
                className="content-viewer-overlay"
                onClick={this.closeMobileContentViewer}
              />
            )}
            <div
              className="patient-right-box1"
              style={{
                display: "flex",
                flexDirection: "row",
                gap: this.state.contentViewerOpen ? "12px" : "0px",
                height: "100%",
              }}
            >
              {/* Left side - Patient Details */}
              <Box
                className="patient-right-content-box"
                style={{
                  width: this.state.contentViewerOpen ? "50%" : "100%",
                  transition: "width 0.3s ease-in-out",
                  overflow: "hidden",
                  display:
                    window.innerWidth <= 991 && this.state.contentViewerOpen
                      ? "none"
                      : "block",
                }}
              >
                <PatientDetailsPanel
                  patient={selectedPatientDetails}
                  loading={loadingDetails}
                  onEdit={this.handleEditModalOpen}
                  onDelete={this.handlePatientDeleted}
                  onRefresh={this.handleRefreshAll}
                  onRefreshDetails={this.handleRefreshDetails}
                  openContentViewer={this.openContentViewer}
                  closeContentViewer={this.closeContentViewer}
                  contentViewerOpen={this.state.contentViewerOpen}
                />
              </Box>

              {/* Right side - Content Viewer */}
              <Box
                className={`content-viewer-inside ${
                  this.state.contentViewerOpen ? "viewer-open" : ""
                }`}
                style={{
                  width: this.state.contentViewerOpen ? "50%" : "0%",
                  transition: "width 0.3s ease-in-out",
                  overflow: "hidden",
                  opacity: this.state.contentViewerOpen ? 1 : 0,
                }}
              >
                <ContentViewerPanel
                  open={this.state.contentViewerOpen}
                  onClose={this.closeContentViewer}
                  content={this.state.viewerContent}
                  contentType={this.state.viewerContentType}
                  fileName={this.state.viewerFileName}
                />
              </Box>
            </div>
          </Box>
        </Box>

        {/* Create Patient Modal */}
        <CreatePatientModal
          open={createModalOpen}
          onClose={this.handleCreateModalClose}
          onPatientCreated={this.handlePatientCreated}
        />

        {/* Edit Patient Modal */}
        <EditPatientModal
          open={editModalOpen}
          onClose={this.handleEditModalClose}
          onPatientUpdated={this.handlePatientUpdated}
          patient={editPatientData}
        />
      </div>
    );
  }
}

export default MyPatients;
